import React, {FC, useCallback} from 'react';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ItemIndexes} from '../../../../../srcCommon/helpers/itemIndexHelpers';
import {MessageDateGetter} from '../../../../helpers/models/messageHelpers';
import {MessageModel} from '../../../../models/api/messageModel';
import {ReselectDirectionsEnum} from '../../../../models/api/preferencesModel';
import {CellsPanelMessagesPageState} from '../../../../store/panels/cells/cellsPanelState';
import {InboxesTeammateMessagesParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  computeIsIdSelected,
  findFirstSelectedId,
  InboxesSelection,
  normalInboxesSelection,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {CellsPanelId} from '../../../../store/panels/panelStateBase';
import {MessageCellClickEventHandler} from '../messageCell';
import {MessageCellContainer} from '../messageCellContainer';
import {
  CellsItemsChildrenRenderer,
  CellsItemsRequestMoreAsyncHandler,
  makeCellsItemsBase,
} from './cellsItemsBase';

/*
 * Props.
 */

export interface CellsMessagesContent {
  messages: Array<MessageModel>;
  messageUids: Array<string>;
  messageIndexes: ItemIndexes;
}

type CellsMessagesParametersPathBuilder = (parameters: InboxesTeammateMessagesParameters) => string;
type CellsMessagesChildrenRenderer = CellsItemsChildrenRenderer;

interface CellsMessagesProps {
  panelId: CellsPanelId;
  hasNextPage: boolean;
  page: CellsPanelMessagesPageState;
  reselectDirection: ReselectDirectionsEnum;
  content: CellsMessagesContent;
  dateGetter: MessageDateGetter;
  buildParametersPath?: CellsMessagesParametersPathBuilder;
  onParametersChange: HandlerOf<InboxesTeammateMessagesParameters>;
  onRetryClick: Handler;
  onRequestMoreAsync: CellsItemsRequestMoreAsyncHandler;
  children: CellsMessagesChildrenRenderer;
}

/*
 * Component.
 */

const CellsItemsBase = makeCellsItemsBase<string>();
export const CellsMessages: FC<CellsMessagesProps> = (props) => {
  const {panelId, page, content, dateGetter, buildParametersPath, onParametersChange} = props;
  const {parameters, cellsContext} = page;
  const {selection: selectionParameter} = parameters;
  const {messages, messageIndexes} = content;

  const focusedId = findFirstSelectedId(selectionParameter);

  const buildSelectionParameters = useCallback(
    (selection: InboxesSelection<string>): InboxesTeammateMessagesParameters => ({
      ...parameters,
      selection,
      activity: undefined,
    }),
    [parameters],
  );

  const buildMessagePath = useCallback(
    (messageUid: string): string | undefined =>
      buildParametersPath
        ? buildParametersPath(buildSelectionParameters(normalInboxesSelection([messageUid])))
        : undefined,
    [buildSelectionParameters, buildParametersPath],
  );

  const onSelectionChange: HandlerOf<InboxesSelection<string>> = useCallback(
    (selection) => onParametersChange(buildSelectionParameters(selection)),
    [buildSelectionParameters, onParametersChange],
  );

  const renderItem = useCallback(
    (id: string, onClick: MessageCellClickEventHandler) => {
      const index = messageIndexes[id];
      const message = messages[index];

      return (
        <MessageCellContainer
          panelId={panelId}
          message={message}
          isSelected={computeIsIdSelected(selectionParameter, message.uid)}
          dateGetter={dateGetter}
          parameters={parameters}
          cellsContext={cellsContext}
          path={buildMessagePath(id)}
          onClick={onClick}
        />
      );
    },
    [
      panelId,
      selectionParameter,
      parameters,
      cellsContext,
      messages,
      messageIndexes,
      dateGetter,
      buildMessagePath,
    ],
  );

  return (
    <CellsItemsBase
      panelId={props.panelId}
      hasNextPage={props.hasNextPage}
      page={props.page}
      reselectDirection={props.reselectDirection}
      itemIds={content.messageUids}
      itemIndexes={content.messageIndexes}
      renderItem={renderItem}
      defaultId="0"
      focusedId={focusedId}
      onSelectionChange={onSelectionChange}
      onRetryClick={props.onRetryClick}
      onRequestMoreAsync={props.onRequestMoreAsync}
    >
      {props.children}
    </CellsItemsBase>
  );
};
